import { Link } from "react-router-dom";

/**
 * EnPrex Services — signature EX monogram.
 * Bold slanted gold "E" (three metallic parallelogram bars) meets a two-stroke "X"
 * — silver blade in front, royal-blue blade behind. Original identity built for enterprise.
 */
export function EnprexMark({ size = 44, className = "" }) {
  return (
    <svg
      viewBox="0 0 220 200"
      width={(size * 220) / 200}
      height={size}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="EnPrex Services"
    >
      <defs>
        <linearGradient id="ep-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F6D982" />
          <stop offset="50%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#9A7B18" />
        </linearGradient>
        <linearGradient id="ep-gold-edge" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8A6C10" />
          <stop offset="100%" stopColor="#F6D982" />
        </linearGradient>
        <linearGradient id="ep-silver" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="60%" stopColor="#DCDCDC" />
          <stop offset="100%" stopColor="#8F8F8F" />
        </linearGradient>
        <linearGradient id="ep-blue" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#3B82F6" />
          <stop offset="40%" stopColor="#2563EB" />
          <stop offset="100%" stopColor="#1E3A8A" />
        </linearGradient>
      </defs>

      {/* Blue back-blade of X (top-right → bottom-left, behind everything) */}
      <path d="M 178 8 L 210 8 L 128 190 L 96 190 Z" fill="url(#ep-blue)" />

      {/* Gold slanted E — three stacked parallelogram bars + connecting spine */}
      {/* Top bar */}
      <path d="M 22 34 L 152 34 L 138 66 L 36 66 Z" fill="url(#ep-gold)" />
      <path d="M 22 34 L 152 34 L 149 41 L 27 41 Z" fill="url(#ep-gold-edge)" opacity="0.5" />
      {/* Left spine (slanted) */}
      <path d="M 22 34 L 36 34 L 20 176 L 6 176 Z" fill="url(#ep-gold)" />
      {/* Middle bar */}
      <path d="M 32 92 L 128 92 L 116 122 L 28 122 Z" fill="url(#ep-gold)" />
      <path d="M 32 92 L 128 92 L 125 99 L 32 99 Z" fill="url(#ep-gold-edge)" opacity="0.5" />
      {/* Bottom bar */}
      <path d="M 12 154 L 166 154 L 152 186 L 6 186 Z" fill="url(#ep-gold)" />
      <path d="M 12 154 L 166 154 L 163 161 L 15 161 Z" fill="url(#ep-gold-edge)" opacity="0.5" />

      {/* Silver front-blade of X (top-left → bottom-right, front) */}
      <path d="M 108 8 L 140 8 L 220 190 L 188 190 Z" fill="url(#ep-silver)" />
    </svg>
  );
}

/**
 * Wordmark: refined "EnPrex" + gold hairline divider + wide-tracked "Services".
 * Premium, global, timeless enterprise typography lockup.
 */
export function EnprexLogo({ size = 42, testid = "brand-logo" }) {
  return (
    <Link to="/" data-testid={testid} className="flex items-center gap-4 group">
      <EnprexMark size={size} className="transition-transform duration-500 group-hover:scale-[1.03]" />
      <div className="leading-none">
        <div className="font-display text-white text-[22px] md:text-[26px] font-medium tracking-[-0.02em]">
          En<span className="font-semibold">Prex</span>
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <span className="block w-4 h-px bg-[#D4AF37]" />
          <span className="text-[10px] text-[#D4AF37] uppercase tracking-[0.45em] font-medium">
            Services
          </span>
        </div>
      </div>
    </Link>
  );
}
